// GENERATED by helm-console/scripts/ensure-style-runtime.mjs — DO NOT EDIT.
// Verbatim copy of helm-bridge/src/client/target-lock.ts. Regenerated every console
// build; edits here are overwritten. Owner correction 2026-09-07: the style
// substrate (backgrounds/canvas, reduce-effects) runs on the Bridge home too.
// target-lock.ts — THE TRAVELLING TARGET LOCK.
//
// Owner ruling (2026-09-08, with screenshot): under STARSHIP and DESTROYER an
// icon button has no border and no fill at rest — the glyph carries the
// intent colour — and on hover a TARGET-LOCK reticle (four corner brackets)
// appears around it. The reticle MOVES from button to button as the pointer
// moves between neighbours: one reticle travelling across the toolbar, not a
// fade-in per button. W1-1 (#721) drew the reticle as a STATIC per-button
// `.ibtn-deco`; measured on that build, moving the pointer from one rail
// button to the next fades A's slot to 0 and B's to 1 — two reticles
// cross-fading, which is exactly the "does not vary by style" the owner
// reported. This module is the travel.
//
// HOW, and why each choice:
//
//   ONE ELEMENT. A single `<span class="ibtn-lock">` on <body>, position:
//   fixed, pointer-events: none. It is positioned with a transform and sized
//   to the hovered button's rect deflated by that button's own
//   `--btn-deco-hover-inset`, and CSS transitions on transform/width/height
//   (styles.css, inside the byte-identical `.ibtn` component block) do the
//   travel. No rAF loop: the only rAF this module ever schedules coalesces a
//   scroll/resize re-measure, at most one in flight.
//
//   IT WEARS THE TARGET'S OWN RETICLE. `--btn-deco-hover` / `--btn-deco-shadow`
//   are read off the hovered button (getComputedStyle hands back the token
//   stream with every var() already substituted — the intent colour, the
//   theme's accent) and written inline on the overlay, whose stylesheet rule
//   is `background: var(--btn-deco-hover)`. So Starship's accent brackets and
//   Destroyer's radar-green/amber ones are the SAME element wearing a
//   different declaration, and a style edits its reticle in its own CSS
//   without touching this file. The hovered button's static `.ibtn-deco` is
//   switched off INLINE while the lock stands on it (removed on leave), so
//   there is never a second reticle under the travelling one.
//
//   A STYLE OPTS IN FROM ITS CSS. The runtime does nothing for a button whose
//   computed `--btn-deco-travel` is not `1`. That token is declared on the
//   style's `.ibtn` scope beside its `--btn-deco-hover`, reaches the Bridge
//   home through the same generator that carries the rest of the seam, and
//   is cross-checked by scripts/style-contrast.mjs: a `decoModel: 'reticle'`
//   row whose scope declares no travel token is refused by name.
//
//   ADJACENT MEANS ADJACENT. The lock travels when the next button is entered
//   within GRACE_MS of leaving the last one AND their centres are within
//   TRAVEL_MAX_PX; otherwise it SNAPS (transition suppressed for one frame)
//   — a reticle gliding across the whole viewport to an unrelated cluster is
//   not a target lock, it is a distraction.
//
//   PRESS TIGHTENS. pointerdown on the target re-places the lock at
//   `--btn-deco-active-inset` (#721's token); pointerup/cancel restores it.
//
//   INERT UNDER EVERY REDUCE LANE. effectsReduced() (OS motion, OS
//   transparency, the app's own `data-reduce` seat) makes every entry point a
//   no-op and clears a standing lock the moment a lane flips on — the static
//   per-button slot is the fallback and it is what the reduce lanes render,
//   instantly, with no travel. The CSS guard strikes the transition too, so a
//   race between the seat flipping and a lock in flight cannot animate.
//
// The console (Bridge home) runs a verbatim copy of this file laid down by
// helm-console/scripts/ensure-style-runtime.mjs — the same route glass-hover.ts
// takes — installed from style-substrate.ts.

import { effectsReduced, onEffectsChange } from './effects-seat.js';

/** The overlay's class. Named once: the stylesheet, the probe and the smoke
 * all read it from here. */
export const LOCK_CLASS = 'ibtn-lock';
/** The token a style declares (`--btn-deco-travel: 1`) to opt its reticle
 * into travelling. */
export const TRAVEL_TOKEN = '--btn-deco-travel';
/** The decoration tokens copied from the target onto the overlay. */
export const LOCK_VARS = ['--btn-deco-hover', '--btn-deco-shadow'] as const;
/** Centre-to-centre distance (CSS px) beyond which a new target is a new
 * cluster and the lock snaps instead of travelling. */
export const TRAVEL_MAX_PX = 96;
/** How long the lock survives leaving a button. A neighbour entered inside
 * this window is a TRAVEL; after it the lock has faded and the next button
 * gets a fresh lock. */
export const GRACE_MS = 160;

const BTN = '.ibtn';
const DECO = '.ibtn-deco';

let installed = false;
let host: HTMLElement | null = null;
let lock: HTMLElement | null = null;
let target: HTMLElement | null = null;
let pressed = false;
let raf = 0;
let hideTimer: ReturnType<typeof setTimeout> | 0 = 0;
/** The button rect the lock was last placed for. A scroll/resize re-measure
 * re-places ONLY when this moved: measured 2026-09-08 on the Helm, Playwright's
 * hover() scrolls the target into view first, the scroll event queued a SNAP
 * re-place, and that frame landed after the travel had started — cancelling
 * the transition and turning every Helm travel into a jump while the Bridge
 * home (nothing to scroll) travelled. */
let placedFor: { x: number; y: number; w: number; h: number } | null = null;

function btnOf(t: EventTarget | null): HTMLElement | null {
  const el = t as Element | null;
  return el && typeof el.closest === 'function' ? (el.closest(BTN) as HTMLElement | null) : null;
}

function actionable(btn: HTMLElement): boolean {
  if ((btn as HTMLButtonElement).disabled) return false;
  const st = btn.getAttribute('data-btn-state');
  if (st === 'disabled' || st === 'busy') return false;
  return !btn.classList.contains('working');
}

/** Does this button's style ask for a travelling lock? One computed-style
 * read per pointerover on an icon button; under Blueprint the token is
 * empty and this returns false on the first line. */
export function travels(btn: HTMLElement): boolean {
  return getComputedStyle(btn).getPropertyValue(TRAVEL_TOKEN).trim() === '1';
}

function ensureLock(): HTMLElement {
  if (lock && lock.isConnected) return lock;
  lock = document.createElement('span');
  lock.className = LOCK_CLASS;
  lock.setAttribute('aria-hidden', 'true');
  document.body.appendChild(lock);
  return lock;
}

function centre(el: HTMLElement): { x: number; y: number } {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

function px(v: string): number {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

/** Position, size and dress the lock for `btn`. `snap` suppresses the travel
 * transition for this placement (a new cluster, a scroll, the first lock). */
function place(btn: HTMLElement, snap: boolean): void {
  const el = ensureLock();
  const cs = getComputedStyle(btn);
  const hoverInset = px(cs.getPropertyValue('--btn-deco-hover-inset')) || px(cs.getPropertyValue('--btn-deco-inset'));
  const activeInset = px(cs.getPropertyValue('--btn-deco-active-inset')) || hoverInset;
  const inset = pressed ? activeInset : hoverInset;
  const r = btn.getBoundingClientRect();
  placedFor = { x: r.left, y: r.top, w: r.width, h: r.height };
  if (snap) el.style.transition = 'none';
  for (const name of LOCK_VARS) {
    const v = cs.getPropertyValue(name).trim();
    if (v) el.style.setProperty(name, v);
    else el.style.removeProperty(name);
  }
  el.style.borderRadius = cs.borderRadius;
  el.style.width = `${Math.max(0, r.width - inset * 2).toFixed(1)}px`;
  el.style.height = `${Math.max(0, r.height - inset * 2).toFixed(1)}px`;
  el.style.transform = `translate(${(r.left + inset).toFixed(1)}px, ${(r.top + inset).toFixed(1)}px)`;
  el.dataset.on = '1';
  if (snap) {
    void el.offsetWidth; // commit the snapped geometry before the transition returns
    el.style.transition = '';
  }
  const deco = btn.querySelector(DECO) as HTMLElement | null;
  if (deco) deco.style.setProperty('opacity', '0');
}

function release(btn: HTMLElement | null): void {
  const deco = btn?.querySelector(DECO) as HTMLElement | null;
  if (deco) deco.style.removeProperty('opacity');
}

function cancelHide(): void {
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = 0;
  }
}

/** Drop the lock now: hide the overlay, restore the target's own slot. */
export function clearTargetLock(): void {
  cancelHide();
  if (raf) {
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
    raf = 0;
  }
  release(target);
  target = null;
  placedFor = null;
  pressed = false;
  if (lock) lock.dataset.on = '0';
}

function scheduleHide(): void {
  cancelHide();
  hideTimer = setTimeout(() => {
    hideTimer = 0;
    clearTargetLock();
  }, GRACE_MS);
}

function onPointerOver(e: PointerEvent): void {
  const btn = btnOf(e.target);
  if (!btn || !actionable(btn) || effectsReduced() || !travels(btn)) {
    if (target && (!btn || btn !== target)) scheduleHide();
    return;
  }
  if (btn === target) {
    cancelHide();
    return;
  }
  const prev = target;
  const standing = !!prev && !!lock && lock.dataset.on === '1';
  let snap = !standing;
  if (standing && prev) {
    const a = centre(prev);
    const b = centre(btn);
    snap = Math.hypot(a.x - b.x, a.y - b.y) > TRAVEL_MAX_PX;
  }
  cancelHide();
  release(prev);
  target = btn;
  pressed = false;
  place(btn, snap);
}

function onPointerOut(e: PointerEvent): void {
  if (!target) return;
  const to = e.relatedTarget as Node | null;
  if (to && target.contains(to)) return; // moved between the glyph and the slot — not a leave
  scheduleHide();
}

function onPointerDown(e: PointerEvent): void {
  if (!target || !target.contains(e.target as Node)) return;
  pressed = true;
  place(target, false);
}

function onPointerUp(): void {
  if (!target || !pressed) return;
  pressed = false;
  place(target, false);
}

function onMoved(): void {
  if (!target || raf || typeof requestAnimationFrame !== 'function') return;
  raf = requestAnimationFrame(() => {
    raf = 0;
    if (!target) return;
    const r = target.getBoundingClientRect();
    const moved = !placedFor
      || Math.abs(r.left - placedFor.x) > 0.5 || Math.abs(r.top - placedFor.y) > 0.5
      || Math.abs(r.width - placedFor.w) > 0.5 || Math.abs(r.height - placedFor.h) > 0.5;
    if (moved) place(target, true);
  });
}

/**
 * Install the delegated listeners once. Returns the boot-line fragment so the
 * caller can log what actually happened — an unnamed runtime seam is how a
 * behaviour ships uninstalled.
 */
export function installTargetLock(root: HTMLElement | null): string {
  if (installed) return 'target lock already installed';
  if (!root || typeof document === 'undefined') return 'target lock NOT installed (no root)';
  host = root;
  host.addEventListener('pointerover', onPointerOver as EventListener, { passive: true });
  host.addEventListener('pointerout', onPointerOut as EventListener, { passive: true });
  host.addEventListener('pointerdown', onPointerDown as EventListener, { passive: true });
  host.addEventListener('pointerup', onPointerUp, { passive: true });
  host.addEventListener('pointercancel', onPointerUp, { passive: true });
  document.addEventListener('scroll', onMoved, { capture: true, passive: true });
  window.addEventListener('resize', onMoved, { passive: true });
  onEffectsChange(() => {
    if (effectsReduced()) clearTargetLock();
  });
  installed = true;
  return `target lock installed (travel token ${TRAVEL_TOKEN}, grace ${GRACE_MS}ms, snap beyond ${TRAVEL_MAX_PX}px)`;
}

/** The overlay element, for the probe and the smoke. */
export function targetLockElement(): HTMLElement | null {
  return lock;
}

/** The button the lock is standing on. */
export function targetLockTarget(): HTMLElement | null {
  return target;
}

/** Test seam: unbind everything and drop the element. */
export function resetTargetLock(): void {
  clearTargetLock();
  if (host) {
    host.removeEventListener('pointerover', onPointerOver as EventListener);
    host.removeEventListener('pointerout', onPointerOut as EventListener);
    host.removeEventListener('pointerdown', onPointerDown as EventListener);
    host.removeEventListener('pointerup', onPointerUp);
    host.removeEventListener('pointercancel', onPointerUp);
  }
  if (typeof document !== 'undefined') document.removeEventListener('scroll', onMoved, { capture: true } as EventListenerOptions);
  if (typeof window !== 'undefined') window.removeEventListener('resize', onMoved);
  if (lock) lock.remove();
  lock = null;
  host = null;
  installed = false;
}
